import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'
import { Position, PositionRange } from '../../types'
import { useLocalStorage } from 'usehooks-ts'
import { StorageKey } from '../../enums/StorageKey'
import { CutterContext } from './CutterContext'
import { useElementRect } from '../../hooks/useElementRect'
import { AudioPlayer, useAudioPlayer } from '../hooks/useAudioPlayer'
import { useZoom } from '../hooks/useZoom'
import { ObservableValue } from '../../state/ObservableValue'

export interface InternalContextValue {
  internalSelection: PositionRange | null
  setInternalSelection: Dispatch<SetStateAction<PositionRange | null>>
  containerElement: HTMLElement | null
  setContainerElement: Dispatch<SetStateAction<HTMLElement | null>>
  backgroundElement: HTMLElement | null
  setBackgroundElement: Dispatch<SetStateAction<HTMLElement | null>>
  timelineElement: HTMLElement | null
  setTimelineElement: Dispatch<SetStateAction<HTMLElement | null>>
  channelsElementRect: DOMRect | null
  autoplayEnabled: boolean
  setAutoplayEnabled: Dispatch<SetStateAction<boolean>>
  rulersEnabled: boolean
  setRulersEnabled: Dispatch<SetStateAction<boolean>>
  mousePosition: ObservableValue<Position | null>
  mouseCursorEnabled: boolean
  setMouseCursorEnabled: Dispatch<SetStateAction<boolean>>
  mouseSelection: PositionRange | null
  setMouseSelection: Dispatch<SetStateAction<PositionRange | null>>
  playbackCursorPosition: Position | null
  setPlaybackCursorPosition: Dispatch<SetStateAction<Position | null>>
  audioPlayer: AudioPlayer
  zoom: number
  setZoom: (value: SetStateAction<number>, position?: Position) => void
}

export const InternalContext = createContext<InternalContextValue>({
  internalSelection: null,
  setInternalSelection() {},
  containerElement: null,
  setContainerElement() {},
  backgroundElement: null,
  setBackgroundElement() {},
  timelineElement: null,
  setTimelineElement() {},
  channelsElementRect: null,
  autoplayEnabled: false,
  setAutoplayEnabled() {},
  rulersEnabled: false,
  setRulersEnabled() {},
  mousePosition: {} as ObservableValue<Position | null>,
  mouseCursorEnabled: true,
  setMouseCursorEnabled() {},
  mouseSelection: null,
  setMouseSelection() {},
  playbackCursorPosition: null,
  setPlaybackCursorPosition() {},
  audioPlayer: {} as AudioPlayer,
  zoom: 1,
  setZoom() {},
})

export function useInternalContext(): InternalContextValue {
  const { selection } = useContext(CutterContext)
  const [internalSelection, setInternalSelection] = useState<PositionRange | null>(null)
  const [backgroundElement, setBackgroundElement] = useState<HTMLElement | null>(null)
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null)
  const [timelineElement, setTimelineElement] = useState<HTMLElement | null>(null)
  const channelsElementRect = useElementRect(backgroundElement)
  const [rulersEnabled, setRulersEnabled] = useLocalStorage(StorageKey.Rulers, false)
  const [autoplayEnabled, setAutoplayEnabled] = useLocalStorage(StorageKey.Autoplay, false)
  const [mouseCursorEnabled, setMouseCursorEnabled] = useState(true)
  const mousePosition = useMemo(() => new ObservableValue<Position | null>(null), [])
  const [mouseSelection, setMouseSelection] = useState<PositionRange | null>(null)
  const [playbackCursorPosition, setPlaybackCursorPosition] = useState<Position | null>(null)
  const audioPlayer = useAudioPlayer()
  const [zoom, setZoom] = useZoom()

  useLayoutEffect(() => setInternalSelection(selection), [selection])

  return useMemo(
    () => ({
      internalSelection,
      setInternalSelection,
      containerElement,
      setContainerElement,
      backgroundElement,
      setBackgroundElement,
      timelineElement,
      setTimelineElement,
      channelsElementRect,
      autoplayEnabled,
      setAutoplayEnabled,
      rulersEnabled,
      setRulersEnabled,
      mouseCursorEnabled,
      mousePosition,
      setMouseCursorEnabled,
      mouseSelection,
      setMouseSelection,
      playbackCursorPosition,
      setPlaybackCursorPosition,
      audioPlayer,
      zoom,
      setZoom,
    }),
    [
      internalSelection,
      containerElement,
      backgroundElement,
      timelineElement,
      channelsElementRect,
      autoplayEnabled,
      setAutoplayEnabled,
      rulersEnabled,
      setRulersEnabled,
      mousePosition,
      mouseCursorEnabled,
      mouseSelection,
      playbackCursorPosition,
      audioPlayer,
      zoom,
      setZoom,
    ],
  )
}

export const InternalContextProvider: FC<PropsWithChildren> = ({ children }) => (
  <InternalContext.Provider value={useInternalContext()}>{children}</InternalContext.Provider>
)
